import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../../../config';
import {
  daysInclusive,
  getEquipmentDailyRate,
  buildOfferEquipRows,
  formatLocalDate,
  offerTermDays,
} from '../utils';
import { fetchClientByEmail } from '../clientApi';
import { fetchPriceQuote, quoteKey } from '../../../utils/priceQuote';
import { dniPlatne, dniPlatneZWyceny } from '../../../utils/dniPlatne';
import { useTabelaDniPlatnych } from '../../../utils/useTabelaDniPlatnych';
import { useTranslation } from '../../../translations';
import {
  amountOrZero, amountToRaw, discountInPln, discountError, advanceError,
  amountErrorMessage, roundToCents,
} from '../../../utils/amountField';
import useAmountChecks from '../../../utils/useAmountChecks';

const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  // Rabat i zaliczka to SUROWY TEKST pola (U.26, jak w Nowej rezerwacji): liczby powstają niżej
  // przez utils/amountField.js, a do zapisu idą wyłącznie one.
  discount: '',
  discountUnit: '%',
  advanceAmount: '',
};

export default function useOfferForm({ setMessage, setError, setFieldErrors, scrollToTop }) {
  const navigate = useNavigate();
  const { t, currentLanguage } = useTranslation();
  // Które pola kwot pokazują limit; sam komunikat jest wyliczany niżej z bieżących wartości.
  const {
    shown: amountChecksShown, show: showAmountCheck, hide: hideAmountCheck,
    showAll: showAmountChecks, hideAll: hideAmountChecks,
  } = useAmountChecks();

  const [form, setForm] = useState(INITIAL_FORM);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [currentOfferId, setCurrentOfferId] = useState(null);
  const [availabilityWarning, setAvailabilityWarning] = useState('');
  const [priceQuote, setPriceQuote] = useState(null); // P7.2: { key, data } from /api/price-quote
  const [quotePending, setQuotePending] = useState(false); // P7.5: debounced fetch in flight
  const tabelaDniPlatnych = useTabelaDniPlatnych();

  const selectedEquipmentRef = useRef(selectedEquipment);
  useEffect(() => {
    selectedEquipmentRef.current = selectedEquipment;
  }, [selectedEquipment]);

  // Snapshot for edit-in-modal (restoring main form when cancel is pressed)
  const offerModalSnapshotRef = useRef(null);

  const startOfToday = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // Computed rental totals
  const days = daysInclusive(dateFrom, dateTo);
  const selectedEq = equipmentList.filter(eq => selectedEquipment.includes(eq.id));
  const isLongTermRental = days > 7;
  const currentQuoteKey = quoteKey(selectedEquipment, formatLocalDate(dateFrom), formatLocalDate(dateTo));
  // P7.5: whole fresh quote response (items/bundles/appliedRules), not just grandTotal.
  const priceQuoteData = (priceQuote && priceQuote.key === currentQuoteKey) ? priceQuote.data : null;
  // N.3: dni płatne z wyceny serwera, a do jej nadejścia i po jej błędzie lokalny zapas. Ta wartość
  // idzie też do wierszy dokumentu i do zapisanej oferty, więc bywa ułamkiem.
  const chargeableDays = dniPlatneZWyceny(priceQuoteData, dniPlatne(days, tabelaDniPlatnych));
  const quotedTotal = priceQuoteData ? priceQuoteData.grandTotal : null;
  // Cena lokalna zaokrąglona do grosza: suma stawek bywa ułamkiem tuż poniżej kwoty z ekranu,
  // a z tą liczbą porównywane są limity rabatu i zaliczki.
  const totalPrice = quotedTotal != null
    ? quotedTotal
    : roundToCents(selectedEq.reduce((sum, eq) => sum + getEquipmentDailyRate(eq, isLongTermRental), 0) * chargeableDays);
  const totalBail = selectedEq.reduce((sum, eq) => sum + Number(eq.bail || 0), 0);

  // Rabat w zł wg jednostki, zawsze w granicach 0…wartość wynajmu (utils/amountField.js).
  const discountNumber = amountOrZero(form.discount);
  const discountValue = discountInPln(form.discount, form.discountUnit, totalPrice);
  const toPay = Math.max(0, totalPrice - discountValue);
  const advanceAmount = amountOrZero(form.advanceAmount);
  const effectiveToPay = Number(toPay || 0);

  // Kody błędów pól kwot liczone z BIEŻĄCYCH wartości i limitu (komunikat nie jest stanem, patrz
  // utils/useAmountChecks.js). Rabat porównujemy z wartością wynajmu, a zaliczkę z kwotą PO
  // rabacie, jak w rezerwacji (decyzja Piotrka 11.10.2026: dawniej limitem była wartość przed
  // rabatem, więc oferta potrafiła prosić o zaliczkę większą niż „Razem do zapłaty”). Dopóki
  // wartość jest nieznana (0: brak sprzętu albo dat), nie ma z czym porównać.
  const discountCode = discountError(form.discount, form.discountUnit, totalPrice);
  const advanceCode = advanceError(form.advanceAmount, toPay, totalPrice > 0);
  const amountI18n = { t, locale: currentLanguage };
  const discountErrorText = amountChecksShown.discount
    ? amountErrorMessage(discountCode, totalPrice, amountI18n)
    : null;
  const advanceErrorText = amountChecksShown.advance
    ? amountErrorMessage(advanceCode, toPay, amountI18n)
    : null;

  // Pola kwot niczego nie przycinają w trakcie pisania: pisanie chowa limit pola, a pokazuje go
  // opuszczenie pola, zmiana jednostki (150 w zł może być poprawne, 150% już nie) i próba zapisu.
  const setDiscount = (raw) => {
    setForm(prev => ({ ...prev, discount: raw }));
    hideAmountCheck('discount');
  };
  const setAdvance = (raw) => {
    setForm(prev => ({ ...prev, advanceAmount: raw }));
    hideAmountCheck('advance');
  };
  const setDiscountUnit = (unit) => {
    setForm(prev => ({ ...prev, discountUnit: unit }));
    showAmountCheck('discount');
  };
  const checkDiscountOnBlur = () => showAmountCheck('discount');
  const checkAdvanceOnBlur = () => showAmountCheck('advance');

  const isOfferIncomplete =
    !form.firstName.trim() ||
    !form.lastName.trim() ||
    !form.email.trim() ||
    !dateFrom ||
    !dateTo ||
    selectedEquipment.length === 0;

  // Kaucja oferty z Historii: zapisana przy ofercie, zamrożona jak kwoty i wiersze (N.6, 3.11.2026).
  // Dotąd brała sumę BIEŻĄCYCH kaucji sprzętu, więc po zmianie kaucji w magazynie okno, kopia PDF
  // i ponowna wysyłka mailem (która zapisuje ją przy ofercie) miały inną kaucję niż wysłana oferta.
  // Suma kaucji sprzętu tylko dla oferty bez zapisanej kaucji.
  const computeOfferDeposit = (offer) => {
    if (!offer) return 0;
    const zapisana = offer.deposit ?? offer.depozyt;
    if (zapisana !== null && zapisana !== undefined && zapisana !== '' && Number.isFinite(Number(zapisana))) {
      return Number(zapisana);
    }
    const equipment = Array.isArray(offer.equipment) ? offer.equipment : [];
    return equipment.reduce((acc, item) => acc + Number(item?.bail || 0), 0);
  };

  // P7.2: debounced server quote (org price rules); falls back to the local calc
  // above on error or while in flight. Server applies the same paid days as this page,
  // so results match exactly with no rules configured.
  useEffect(() => {
    const from = formatLocalDate(dateFrom);
    const to = formatLocalDate(dateTo);
    if (!selectedEquipment.length || !from || !to) { setPriceQuote(null); setQuotePending(false); return undefined; }
    const key = quoteKey(selectedEquipment, from, to);
    let cancelled = false;
    setQuotePending(true);
    const timer = setTimeout(async () => {
      const data = await fetchPriceQuote(selectedEquipment, from, to);
      if (!cancelled) {
        setPriceQuote(data ? { key, data } : null);
        setQuotePending(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [selectedEquipment, dateFrom, dateTo]);

  // Availability check on date/equipment change
  useEffect(() => {
    if (!dateFrom || !dateTo || selectedEquipmentRef.current.length === 0) {
      setAvailabilityWarning('');
      return;
    }

    let cancelled = false;
    const verifyAvailability = async () => {
      try {
        const params = new URLSearchParams({
          start: formatLocalDate(dateFrom),
          end: formatLocalDate(dateTo),
          ...(currentOfferId ? { excludeOfferId: String(currentOfferId) } : {})
        });
        const res = await axios.get(`${API_BASE}/api/equipment/available?${params}`, { withCredentials: true });
        if (cancelled) return;

        setEquipmentList(res.data);
        const availableIds = new Set(res.data.map(eq => eq.id));
        const removed = selectedEquipmentRef.current.filter(id => !availableIds.has(id));

        if (removed.length) {
          setSelectedEquipment(prev => prev.filter(id => availableIds.has(id)));
          setAvailabilityWarning('Część wybranego sprzętu została usunięta, ponieważ w tym terminie jest już zarezerwowana.');
          scrollToTop();
        } else {
          setAvailabilityWarning('');
        }
      } catch {
        if (!cancelled) {
          setAvailabilityWarning('Nie udało się potwierdzić dostępności sprzętu. Spróbuj ponownie.');
          scrollToTop();
        }
      }
    };

    verifyAvailability();
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, currentOfferId]);

  const validateOfferInputs = useCallback(() => {
    // Oferta nie wychodzi w trakcie przeliczania: cena i wiersze byłyby ze stawek bazowych,
    // bez cennika i zestawów, które za chwilę doliczy serwer.
    if (quotePending) {
      setError(t('generateOffer.quotePending'));
      scrollToTop();
      return false;
    }
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setError('Uzupełnij dane klienta');
      scrollToTop();
      return false;
    }
    if (!dateFrom || !dateTo) {
      setError('Wybierz termin wynajmu');
      scrollToTop();
      return false;
    }
    if (selectedEquipment.length === 0) {
      setError('Dodaj przynajmniej jedną pozycję sprzętu');
      scrollToTop();
      return false;
    }
    // Limity kwot (U.26): pola niczego nie przycinają w trakcie pisania, więc tu jest ostatnia
    // kontrola przed wysyłką; konkretny limit („Maksymalnie X") pokazują oba pola.
    if (discountCode || advanceCode) {
      showAmountChecks();
      setError(t('amountField.error.checkAmounts'));
      scrollToTop();
      return false;
    }
    return true;
  }, [form, dateFrom, dateTo, selectedEquipment, discountCode, advanceCode, quotePending, showAmountChecks, setError, scrollToTop, t]);

  const buildOfferData = useCallback((extra = {}) => {
    const subtotalVal = Number(totalPrice || 0);
    const razemVal = Number(toPay || 0);
    const zaliczkaVal = Number(advanceAmount || 0);

    // Wiersze z tej samej wyceny co „CENA” (cennik i zestawy); ta sama funkcja składa wiersze
    // oferty z Historii (utils.js#buildOfferEquipRows).
    const equipRows = buildOfferEquipRows(selectedEq, priceQuoteData, chargeableDays, isLongTermRental);

    return {
      dataOferty: new Date().toLocaleDateString('pl-PL'),
      nazwaKlienta: `${form.firstName.trim()} ${form.lastName.trim()}`,
      emailKlienta: form.email.trim(),
      terminOd: formatLocalDate(dateFrom),
      terminDo: formatLocalDate(dateTo),
      equip: equipRows,
      selectedEquipment: selectedEq.map(item => ({
        id: item.id,
        category: item.category,
        class: item.class,
        brand: item.brand,
        size: item.size,
        inventory_number: item.inventory_number,
        bail: Number(item.bail || 0),
        price_per_day: getEquipmentDailyRate(item, isLongTermRental),
        quantity: 1
      })),
      cenaCalosc: subtotalVal.toFixed(2),
      rabat: discountValue.toFixed(2),
      razemCena: razemVal.toFixed(2),
      zaliczka: zaliczkaVal.toFixed(2),
      depozyt: Number(totalBail || 0).toFixed(2),
      deposit: Number(totalBail || 0),
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      startDate: formatLocalDate(dateFrom),
      endDate: formatLocalDate(dateTo),
      subtotal: subtotalVal.toFixed(2),
      razemDoZaplaty: razemVal.toFixed(2),
      discountAmount: discountValue.toFixed(2),
      discountUnit: form.discountUnit,
      discount_unit: form.discountUnit,
      days: chargeableDays,
      ...extra
    };
  }, [form, dateFrom, dateTo, selectedEq, chargeableDays, isLongTermRental, totalPrice, toPay, advanceAmount, discountValue, totalBail, priceQuoteData]);

  const openEquipmentModal = async () => {
    if (!dateFrom || !dateTo) {
      setError('Najpierw wybierz termin wynajmu');
      scrollToTop();
      return;
    }
    try {
      const params = new URLSearchParams({
        start: formatLocalDate(dateFrom),
        end: formatLocalDate(dateTo),
        ...(currentOfferId ? { excludeOfferId: String(currentOfferId) } : {})
      });
      const res = await axios.get(`${API_BASE}/api/equipment/available?${params}`, { withCredentials: true });
      setEquipmentList(res.data);
      setShowEquipmentModal(true);
      setError('');
    } catch {
      setError('Nie udało się pobrać dostępnego sprzętu');
      scrollToTop();
    }
  };

  const removeEquipment = (equipmentId) => {
    setSelectedEquipment(prev => prev.filter(id => id !== equipmentId));
  };

  const clearFormInputs = () => {
    setForm(INITIAL_FORM);
    setDateFrom(null);
    setDateTo(null);
    setSelectedEquipment([]);
    setCurrentOfferId(null);
    setAvailabilityWarning('');
    setFieldErrors({});
    hideAmountChecks();
  };

  const resetOfferForm = () => {
    setForm(INITIAL_FORM);
    setDateFrom(null);
    setDateTo(null);
    setSelectedEquipment([]);
    setCurrentOfferId(null);
    setAvailabilityWarning('');
    hideAmountChecks();
  };

  const _applyOfferToForm = (offer) => {
    setCurrentOfferId(offer.id || null);
    const nameParts = String(offer.client_name || '').trim().split(/\s+/);
    const firstName = offer.firstname || offer.firstName || (nameParts[0] || '');
    const lastName = offer.lastname || offer.lastName || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '');

    const storedDiscountAmount = Number(offer.discount_amount ?? offer.discountAmount ?? 0) || 0;
    const storedSubtotal = Number(offer.subtotal ?? offer.cenaCalosc ?? 0) || 0;
    const storedUnit = String(offer.discount_unit || offer.discountUnit || 'PLN').toUpperCase() === '%' ? '%' : 'PLN';
    let discountForForm = 0;
    if (storedUnit === '%') {
      discountForForm = storedSubtotal > 0
        ? Number(((storedDiscountAmount / storedSubtotal) * 100).toFixed(2))
        : storedDiscountAmount || 0;
    } else {
      discountForForm = storedDiscountAmount;
    }

    setForm(prev => ({
      ...prev,
      firstName: firstName || prev.firstName,
      lastName: lastName || prev.lastName,
      email: offer.email || prev.email,
      // Liczby z oferty zamieniamy na tekst pola (0 → puste pole z podpowiedzią).
      discount: amountToRaw(discountForForm) || prev.discount,
      discountUnit: storedUnit || prev.discountUnit || '%',
      advanceAmount: amountToRaw(Number(offer.advance_amount) || 0)
    }));
    hideAmountChecks();

    setDateFrom(offer.date_from ? new Date(offer.date_from) : null);
    setDateTo(offer.date_to ? new Date(offer.date_to) : null);

    if (Array.isArray(offer.equipment) && offer.equipment.length) {
      setEquipmentList(offer.equipment);
    }

    if (Array.isArray(offer.equipment_ids) && offer.equipment_ids.length) {
      setSelectedEquipment(offer.equipment_ids.filter(Boolean));
    } else if (Array.isArray(offer.equipment) && offer.equipment.length) {
      setSelectedEquipment(offer.equipment.map(eq => eq?.id).filter(Boolean));
    } else {
      setSelectedEquipment([]);
    }

    setAvailabilityWarning('');
  };

  const loadOfferIntoForm = (offer) => {
    if (!offer) return;
    _applyOfferToForm(offer);
    scrollToTop();
  };

  const loadOfferIntoFormSilent = (offer) => {
    if (!offer) return;
    // Save snapshot so cancelEditOfferInModal can restore it
    offerModalSnapshotRef.current = {
      form,
      dateFrom,
      dateTo,
      selectedEquipment,
      equipmentList,
      currentOfferId,
    };
    _applyOfferToForm(offer);
  };

  const restoreFormSnapshot = () => {
    const snap = offerModalSnapshotRef.current;
    offerModalSnapshotRef.current = null;
    if (!snap) return;
    setForm(snap.form);
    setDateFrom(snap.dateFrom);
    setDateTo(snap.dateTo);
    setSelectedEquipment(snap.selectedEquipment);
    setEquipmentList(snap.equipmentList);
    setCurrentOfferId(snap.currentOfferId);
    hideAmountChecks();
  };

  const createReservationFromOffer = async (offer) => {
    if (!offer) return;

    const normalizeToYmd = (value) => {
      if (!value) return null;
      const str = String(value);
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
      const d = value instanceof Date ? value : new Date(str);
      return Number.isNaN(d.getTime()) ? null : formatLocalDate(d);
    };

    const storedDiscountAmount = Number(
      offer.discount_amount ?? offer.discountAmount ?? offer.discount ?? 0
    ) || 0;
    const storedSubtotal = Number(offer.subtotal ?? offer.subtotal_amount ?? offer.cenaCalosc ?? offer.cena_total ?? 0) || 0;
    const discountUnitRaw = String(offer.discount_unit ?? offer.discountUnit ?? 'PLN').toUpperCase();
    const storedUnit = discountUnitRaw === 'PLN' ? 'PLN' : '%';

    let discountForPrefill = storedDiscountAmount;
    if (storedUnit === '%') {
      discountForPrefill = storedSubtotal > 0
        ? Number(((storedDiscountAmount / storedSubtotal) * 100).toFixed(2))
        : storedDiscountAmount;
    }

    const clientNameTokens = offer.client_name ? String(offer.client_name).trim().split(/\s+/) : [];

    const prefill = {
      dateFrom: normalizeToYmd(offer.date_from),
      dateTo: normalizeToYmd(offer.date_to),
      selectedEquipment: Array.isArray(offer.equipment_ids)
        ? offer.equipment_ids.filter(Boolean)
        : (Array.isArray(offer.equipment) ? offer.equipment.map(eq => eq?.id).filter(Boolean) : []),
      offerId: offer.id,
      // N.6: kwota przed rabatem i rabat w zł, które serwer zapisze przy rezerwacji z tym samym
      // sprzętem i terminem co oferta (GET /api/sent-offers, Login/backend/lib/kwotaOferty.js), oraz
      // dni płatne oferty. Nowa rezerwacja pokazuje je zamiast bieżącej wyceny.
      offerAmount: offer.reservation_amount ?? null,
      offerDiscount: offer.reservation_discount ?? null,
      offerDays: offer.days ?? null,
      discount: discountForPrefill,
      discountUnit: storedUnit,
      advanceAmount: Number(offer.advance_amount || 0) || 0,
      advancePaid: false,
      note: offer.note || '',
      firstname: offer.firstname || offer.firstName || (clientNameTokens.length ? clientNameTokens[0] : '') || '',
      lastname: offer.lastname || offer.lastName || (clientNameTokens.length > 1 ? clientNameTokens.slice(1).join(' ') : '') || '',
      email: offer.email || offer.client_email || '',
      phone: offer.phone || offer.client_phone || ''
    };

    const emailToCheck = offer.email || offer.client_email || '';
    let existingClient = null;
    if (emailToCheck) {
      try {
        existingClient = await fetchClientByEmail(emailToCheck);
      } catch (err) {
        console.warn('Nie udało się wyszukać klienta po adresie e-mail:', err);
      }
    }

    const isMeaningful = (value) => {
      if (value === null || typeof value === 'undefined') return false;
      const str = String(value).trim();
      return str !== '' && str.toLowerCase() !== 'brak danych';
    };

    if (existingClient) {
      const phoneFromClient = existingClient.phone_number || existingClient.phone || existingClient.phoneNumber || '';
      if (isMeaningful(phoneFromClient)) prefill.phone = phoneFromClient;
      if (isMeaningful(existingClient.firstname)) prefill.firstname = existingClient.firstname;
      if (isMeaningful(existingClient.lastname)) prefill.lastname = existingClient.lastname;
      if (isMeaningful(existingClient.email)) prefill.email = existingClient.email;

      const clientPrefill = {};
      if (isMeaningful(existingClient.firstname)) clientPrefill.firstname = existingClient.firstname;
      if (isMeaningful(existingClient.lastname)) clientPrefill.lastname = existingClient.lastname;
      if (isMeaningful(existingClient.email)) clientPrefill.email = existingClient.email;
      if (isMeaningful(phoneFromClient)) clientPrefill.phone_number = phoneFromClient;
      if (isMeaningful(existingClient.id_number)) clientPrefill.id_number = existingClient.id_number;
      if (isMeaningful(existingClient.city)) clientPrefill.city = existingClient.city;
      if (isMeaningful(existingClient.postal_code)) clientPrefill.postal_code = existingClient.postal_code;
      if (isMeaningful(existingClient.street)) clientPrefill.street = existingClient.street;
      if (isMeaningful(existingClient.building_number)) clientPrefill.building_number = existingClient.building_number;
      if (isMeaningful(existingClient.apartment_number)) clientPrefill.apartment_number = existingClient.apartment_number;
      if (existingClient.id) clientPrefill.client_id = existingClient.id;

      if (Object.keys(clientPrefill).length) {
        try { localStorage.setItem('prefillClientData', JSON.stringify(clientPrefill)); } catch {}
      } else {
        try { localStorage.removeItem('prefillClientData'); } catch {}
      }
    } else {
      try { localStorage.removeItem('prefillClientData'); } catch {}
    }

    try {
      localStorage.setItem('prefillReservationFromOffer', JSON.stringify(prefill));
    } catch (e) {
      console.error('Nie udało się zapisać prefillReservationFromOffer:', e);
    }

    navigate('/reservation');
  };

  // Computed values for the selected offer in detail modal
  const getSelectedOfferTotals = (selectedOffer) => {
    if (!selectedOffer || !selectedOffer.date_from || !selectedOffer.date_to) {
      return { totalDays: 0, chargeableDays: 0, isLongTerm: false };
    }
    return offerTermDays(new Date(selectedOffer.date_from), new Date(selectedOffer.date_to), selectedOffer.days);
  };

  return {
    form,
    setForm,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    selectedEquipment,
    setSelectedEquipment,
    equipmentList,
    setEquipmentList,
    showEquipmentModal,
    setShowEquipmentModal,
    currentOfferId,
    setCurrentOfferId,
    availabilityWarning,
    offerModalSnapshotRef,
    startOfToday,
    days,
    chargeableDays,
    selectedEq,
    isLongTermRental,
    totalPrice,
    priceQuoteData,
    quotePending,
    totalBail,
    discountNumber,
    discountErrorText,
    advanceErrorText,
    discountValue,
    toPay,
    advanceAmount,
    effectiveToPay,
    setDiscount,
    setAdvance,
    setDiscountUnit,
    checkDiscountOnBlur,
    checkAdvanceOnBlur,
    isOfferIncomplete,
    computeOfferDeposit,
    validateOfferInputs,
    buildOfferData,
    openEquipmentModal,
    removeEquipment,
    clearFormInputs,
    resetOfferForm,
    loadOfferIntoForm,
    loadOfferIntoFormSilent,
    restoreFormSnapshot,
    createReservationFromOffer,
    getSelectedOfferTotals,
  };
}
